import { useAuth } from "../../services/AuthContext";
import { useCallback, useEffect, useRef, useState } from "react";
import { db } from "../../services/firebase.config";
import {
  collection,
  endAt,
  getDocs,
  limit,
  orderBy,
  query,
  startAt,
} from "firebase/firestore";

export const useUserSearch = (userProfile) => {
  const { currentUserState } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const currentUserRef = useRef(currentUserState);

  useEffect(() => {
    currentUserRef.current = currentUserState;
  }, [currentUserState]);

  const searchUsers = useCallback(
    async (username) => {
      const user = currentUserRef.current;
      if (!username.trim() || !user) {
        setSearchResults([]);
        return;
      }
      setIsSearching(true);
      try {
        const prefix = username.trim().toLowerCase();
        const usersQuery = query(
          collection(db, "public-profiles"),
          orderBy("username"),
          startAt(prefix),
          endAt(prefix + "\uf8ff"),
          limit(20)
        );
        const querySnapshot = await getDocs(usersQuery);
        const allResults = [];
        querySnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          // Exclude only the current user, but include everyone else
          if (
            docSnap.id !== user.uid &&
            !allResults.find((r) => r.id === docSnap.id)
          ) {
            // Add relationship status to each result
            const isFriend = userProfile?.friends?.includes(docSnap.id);
            const hasOutgoingRequest =
              userProfile?.friendRequests?.sent?.includes(docSnap.id);
            const hasIncomingRequest =
              userProfile?.friendRequests?.received?.includes(docSnap.id);

            allResults.push({
              id: docSnap.id,
              ...data,
              relationshipStatus: {
                isFriend,
                hasOutgoingRequest,
                hasIncomingRequest,
              },
            });
          }
        });
        setSearchResults(allResults);
      } catch (_) {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    [userProfile]
  );

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      searchUsers(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery, searchUsers]);

  return { searchQuery, setSearchQuery, isSearching, searchResults };
};
